import { findDocByContent, normalizeContentPath } from './content-manifest';

interface ResolvedHref {
  href: string;
  internal: boolean;
  openInNewTab: boolean;
}

const assetPattern = /\.(?:svg|png|jpe?g|gif|webp|mmd|json)$/i;

function escapeScriptData(value: string) {
  return value.replace(/<\/script/gi, '<\\/script');
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttribute(value: string) {
  return escapeHtml(value).replace(/`/g, '&#96;');
}

function resolveRelativePath(baseDocPath: string, target: string) {
  const baseSegments = normalizeContentPath(baseDocPath)
    .split('/')
    .filter(Boolean);
  baseSegments.pop();

  for (const segment of target.split('/')) {
    if (!segment || segment === '.') {
      continue;
    }

    if (segment === '..') {
      baseSegments.pop();
      continue;
    }

    baseSegments.push(segment);
  }

  return baseSegments.join('/');
}

function resolveHref(baseDocPath: string, target: string): ResolvedHref {
  if (/^(https?:|mailto:|tel:)/i.test(target)) {
    return { href: target, internal: false, openInNewTab: true };
  }

  if (target.startsWith('#')) {
    return { href: target, internal: false, openInNewTab: false };
  }

  const normalizedTarget = resolveRelativePath(baseDocPath, target);

  if (normalizedTarget.endsWith('.md')) {
    const doc = findDocByContent(normalizedTarget);
    if (doc) {
      return {
        href: `/${doc.routePath}`,
        internal: true,
        openInNewTab: false,
      };
    }

    return {
      href: `/content/${normalizedTarget}`,
      internal: false,
      openInNewTab: true,
    };
  }

  return {
    href: `/${normalizedTarget}`,
    internal: false,
    openInNewTab: assetPattern.test(normalizedTarget),
  };
}

function renderLink(label: string, target: string, baseDocPath: string) {
  const resolved = resolveHref(baseDocPath, target);
  const attributes = [
    `href="${escapeAttribute(resolved.href)}"`,
    'class="docs-inline-link"',
  ];

  if (resolved.internal) {
    attributes.push('data-route-link="true"');
  }

  if (resolved.openInNewTab) {
    attributes.push('target="_blank"', 'rel="noopener noreferrer"');
  }

  return `<a ${attributes.join(' ')}>${escapeHtml(label)}</a>`;
}

function renderImage(alt: string, target: string, baseDocPath: string) {
  const resolved = resolveHref(baseDocPath, target);
  const caption = alt ? `<figcaption>${escapeHtml(alt)}</figcaption>` : '';
  return `<figure class="doc-figure"><img src="${escapeAttribute(resolved.href)}" alt="${escapeAttribute(alt)}" loading="lazy" />${caption}</figure>`;
}

function renderRailroadBlock(source: string) {
  return [
    '<figure class="docs-railroad-block" data-railroad-diagram="true">',
    '  <div class="docs-railroad-render" aria-live="polite">',
    '    <p class="docs-railroad-placeholder">Rendering railroad diagram…</p>',
    '  </div>',
    `  <script type="application/json" class="docs-railroad-source">${escapeScriptData(source)}</script>`,
    '</figure>',
  ].join('\n');
}

function renderInline(text: string, baseDocPath: string) {
  const codeTokens: string[] = [];
  let rendered = escapeHtml(text);

  rendered = rendered.replace(/`([^`]+)`/g, (_match, code: string) => {
    const token = `@@CODE${codeTokens.length}@@`;
    codeTokens.push(`<code>${escapeHtml(code)}</code>`);
    return token;
  });

  rendered = rendered.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_match, label: string, target: string) =>
      renderLink(label, target, baseDocPath),
  );
  rendered = rendered.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  rendered = rendered.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  rendered = rendered.replace(/_([^_]+)_/g, '<em>$1</em>');

  rendered = rendered.replace(
    /@@CODE(\d+)@@/g,
    (_match, index: string) => codeTokens[Number(index)] || '',
  );

  return rendered;
}

function isTableSeparator(line: string) {
  return /^\|?(\s*:?-{3,}:?\s*\|)+\s*:?-{3,}:?\s*\|?$/.test(line.trim());
}

function splitTableRow(line: string) {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

function isStandaloneImage(line: string) {
  return /^!\[[^\]]*\]\([^)]*\)$/.test(line.trim());
}

function isBlockStart(line: string, nextLine?: string) {
  const trimmed = line.trim();
  if (!trimmed) return true;
  if (/^#{1,6}\s+/.test(trimmed)) return true;
  if (trimmed.startsWith('```')) return true;
  if (/^>\s?/.test(trimmed)) return true;
  if (/^[-*]\s+/.test(trimmed)) return true;
  if (/^\d+\.\s+/.test(trimmed)) return true;
  if (isStandaloneImage(trimmed)) return true;
  if (nextLine && trimmed.includes('|') && isTableSeparator(nextLine))
    return true;
  return false;
}

export function renderMarkdown(markdown: string, baseDocPath: string) {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const parts: string[] = [];
  let index = 0;
  let skippedFirstH1 = false;

  while (index < lines.length) {
    const rawLine = lines[index] || '';
    const trimmed = rawLine.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    if (trimmed.startsWith('```')) {
      const language = trimmed.slice(3).trim();
      const normalizedLanguage = language.toLowerCase();
      index += 1;
      const codeLines: string[] = [];
      while (
        index < lines.length &&
        !(lines[index] ?? '').trim().startsWith('```')
      ) {
        codeLines.push(lines[index] || '');
        index += 1;
      }
      index += 1;
      if (normalizedLanguage === 'railroad') {
        parts.push(renderRailroadBlock(codeLines.join('\n')));
        continue;
      }
      parts.push(
        `<pre class="docs-code-block"><code${language ? ` data-language="${escapeAttribute(language)}"` : ''}>${escapeHtml(codeLines.join('\n'))}</code></pre>`,
      );
      continue;
    }

    const heading = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      const level = Math.min(6, (heading[1] ?? '').length);
      if (!skippedFirstH1 && level === 1) {
        skippedFirstH1 = true;
        index += 1;
        continue;
      }

      parts.push(
        `<h${level}>${renderInline(heading[2] ?? '', baseDocPath)}</h${level}>`,
      );
      index += 1;
      continue;
    }

    const imageMatch = trimmed.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imageMatch) {
      parts.push(
        renderImage(imageMatch[1] ?? '', imageMatch[2] ?? '', baseDocPath),
      );
      index += 1;
      continue;
    }

    if (
      index + 1 < lines.length &&
      trimmed.includes('|') &&
      isTableSeparator(lines[index + 1] || '')
    ) {
      const header = splitTableRow(trimmed);
      index += 2;
      const rows: string[][] = [];
      while (
        index < lines.length &&
        (lines[index] ?? '').trim().includes('|')
      ) {
        rows.push(splitTableRow(lines[index] || ''));
        index += 1;
      }

      const thead = `<thead><tr>${header.map((cell) => `<th>${renderInline(cell, baseDocPath)}</th>`).join('')}</tr></thead>`;
      const tbody = `<tbody>${rows.map((row) => `<tr>${row.map((cell) => `<td>${renderInline(cell, baseDocPath)}</td>`).join('')}</tr>`).join('')}</tbody>`;
      parts.push(
        `<div class="docs-table-wrap"><table>${thead}${tbody}</table></div>`,
      );
      continue;
    }

    if (/^>\s?/.test(trimmed)) {
      const quoteLines: string[] = [];
      while (
        index < lines.length &&
        /^>\s?/.test((lines[index] ?? '').trim())
      ) {
        quoteLines.push((lines[index] ?? '').trim().replace(/^>\s?/, ''));
        index += 1;
      }
      parts.push(
        `<blockquote>${renderInline(quoteLines.join(' '), baseDocPath)}</blockquote>`,
      );
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (
        index < lines.length &&
        /^[-*]\s+/.test((lines[index] ?? '').trim())
      ) {
        items.push((lines[index] ?? '').trim().replace(/^[-*]\s+/, ''));
        index += 1;
      }
      parts.push(
        `<ul>${items.map((item) => `<li>${renderInline(item, baseDocPath)}</li>`).join('')}</ul>`,
      );
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (
        index < lines.length &&
        /^\d+\.\s+/.test((lines[index] ?? '').trim())
      ) {
        items.push((lines[index] ?? '').trim().replace(/^\d+\.\s+/, ''));
        index += 1;
      }
      parts.push(
        `<ol>${items.map((item) => `<li>${renderInline(item, baseDocPath)}</li>`).join('')}</ol>`,
      );
      continue;
    }

    const paragraphLines: string[] = [];
    while (index < lines.length) {
      const line = (lines[index] ?? '').trim();
      if (!line) {
        break;
      }
      if (paragraphLines.length > 0 && isBlockStart(line, lines[index + 1])) {
        break;
      }
      paragraphLines.push(line);
      index += 1;
    }

    parts.push(`<p>${renderInline(paragraphLines.join(' '), baseDocPath)}</p>`);
  }

  return parts.join('\n');
}
